import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../auth/Auth';
import { useNavigate } from 'react-router-dom';
import './Login.css';

function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [attempts, setAttempts] = useState(0);
  const [lockoutUntil, setLockoutUntil] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const MAX_ATTEMPTS_WARNING = 9;
  const MAX_ATTEMPTS_LOCKOUT = 15;

  useEffect(() => {
    const savedLockout = localStorage.getItem('loginLockoutUntil');
    if (savedLockout) {
      const lockoutTime = parseInt(savedLockout);
      if (lockoutTime > Date.now()) {
        setLockoutUntil(lockoutTime);
        const remainingMinutes = Math.ceil((lockoutTime - Date.now()) / (60 * 1000));
        setError('Занадто багато спроб. Спробуйте через ' + remainingMinutes + ' хвилин');
      } else {
        localStorage.removeItem('loginLockoutUntil');
        localStorage.removeItem('loginAttempts');
      }
    }
    
    const savedAttempts = localStorage.getItem('loginAttempts');
    if (savedAttempts) {
      setAttempts(parseInt(savedAttempts));
    }
  }, []);

  const handleLockout = (attempts) => {
    const now = Date.now();
    let lockoutDuration;
    let message = '';
    
    if (attempts >= MAX_ATTEMPTS_WARNING && attempts < MAX_ATTEMPTS_LOCKOUT) {
      lockoutDuration = 3 * 60 * 1000;
      message = 'Забагато спроб. Спробуйте через 3 хвилини';
    } else if (attempts >= MAX_ATTEMPTS_LOCKOUT) {
      lockoutDuration = 15 * 60 * 1000;
      message = 'Забагато спроб. Спробуйте через 15 хвилин або зверніться до адміністратора';
    }

    if (lockoutDuration) {
      const lockoutUntil = now + lockoutDuration;
      setLockoutUntil(lockoutUntil);
      setError(message);
      localStorage.setItem('loginLockoutUntil', lockoutUntil.toString());
    }
  };

  const handleLogin = async (email, password) => {
    if (lockoutUntil && Date.now() < lockoutUntil) {
      const remainingMinutes = Math.ceil((lockoutUntil - Date.now()) / (60 * 1000));
      const remainingSeconds = Math.ceil((lockoutUntil - Date.now()) / 1000);
      setError('Зачекайте ' + remainingMinutes + ' хв ' + (remainingSeconds % 60) + ' сек перед наступною спробою');
      return;
    }

    setIsLoading(true);
    setError('');
    
    try {
      const response = await axios.post('https://test2.planfix-to-syrve.com/login', { email, password });
      
      if (response.data.deactivated) {
        setError('Ваш акаунт було деактивовано. Зверніться до адміністратора.');
        setIsLoading(false);
        return;
      }
      
      if (response.data.success === false) {
        const newAttempts = attempts + 1;
        setAttempts(newAttempts);
        localStorage.setItem('loginAttempts', newAttempts.toString());
        
        
        setError('Неправильний логін або пароль. Спробуйте ще раз.');
        
        handleLockout(newAttempts);
        setIsLoading(false);
        return;
      }
      
      
      setAttempts(0);
      localStorage.removeItem('loginAttempts');
      localStorage.removeItem('loginLockoutUntil');
      
      await login(email, password);
      window.location.href = '/';
      
    } catch (error) {
      console.error('Помилка запиту:', error);
      
      if (error.response && error.response.status === 401) {
        const newAttempts = attempts + 1;
        setAttempts(newAttempts);
        localStorage.setItem('loginAttempts', newAttempts.toString());
        
        setError('Неправильний логін або пароль. Спробуйте ще раз.');
        handleLockout(newAttempts);
      } else if (error.response) {
        setError('Помилка сервера (' + error.response.status + '). Спробуйте пізніше.');
      } else if (error.request) {
        setError('Сервер не відповідає. Перевірте інтернет-з\'єднання.');
      } else {
        setError('Помилка при виконанні запиту. Спробуйте ще раз.');
      }
      
      setIsLoading(false);
    }
  };

  function handleSubmit(event) {
    event.preventDefault();
    setError('');

    if (!email || !password) {
      setError('Будь ласка, заповніть усі поля');
      return;
    }

    if (!email.includes('@') || !email.includes('.')) {
      setError('Введіть коректний email (наприклад, user@example.com)');
      return;
    }

    handleLogin(email, password);
  }

  const handleForgotPassword = () => {
    navigate('/ForgotPassword');
  };

  const getLockoutMessage = () => {
    if (!lockoutUntil) return null;
    const remainingTime = lockoutUntil - Date.now();
    if (remainingTime <= 0) return null;
    
    const minutes = Math.floor(remainingTime / 60000);
    const seconds = Math.floor((remainingTime % 60000) / 1000);
    
    return 'Заблокувано: ' + minutes + 'хв ' + seconds + 'с';
  };

  return (
    <div className="login-page">
      <div className="login-container">
        <div className="login-header">
          <h1 className="login-title">Вхід</h1>
          <p className="login-subtitle">Увійдіть у свій акаунт</p>
        </div>
        
        <div className="login-card">
          <form onSubmit={handleSubmit} className="login-form">
            <div className="form-group">
              <label htmlFor="email" className="form-label">
                Email <span className="required-star">*</span>
              </label>
              <input
                id="email"
                className="form-input"
                type="email"
                placeholder="Ваш email"
                value={email}
                onChange={e => setEmail(e.target.value)}
                required
                disabled={isLoading || (lockoutUntil && Date.now() < lockoutUntil)}
                autoComplete="email"
              />
            </div>
            
            <div className="form-group">
              <label htmlFor="password" className="form-label">
                Пароль <span className="required-star">*</span>
              </label>
              <input
                id="password"
                className="form-input"
                type="password"
                placeholder="Ваш пароль"
                value={password}
                onChange={e => setPassword(e.target.value)}
                required
                disabled={isLoading || (lockoutUntil && Date.now() < lockoutUntil)}
                autoComplete="current-password"
              />
            </div>
            
            {}
            {error && (
              <div style={{
                display: 'block !important',
                padding: '12px 16px',
                backgroundColor: '#f8d7da',
                border: '1px solid #f5c6cb',
                borderRadius: '8px',
                color: '#721c24',
                fontSize: '14px',
                marginBottom: '15px',
                lineHeight: '1.5',
                fontFamily: "'IBM Plex Mono', monospace",
                textAlign: 'center',
                width: '100%',
                boxSizing: 'border-box'
              }}>
                {error}
                {lockoutUntil && Date.now() < lockoutUntil && (
                  <div style={{
                    marginTop: '8px',
                    paddingTop: '8px',
                    borderTop: '1px dashed rgba(0,0,0,0.1)',
                    fontWeight: '600',
                    fontSize: '15px',
                    color: '#dc3545'
                  }}>
                    {getLockoutMessage()}
                  </div>
                )}
              </div>
            )}
            
            <button 
              type="submit" 
              className="login-button"
              disabled={isLoading || (lockoutUntil && Date.now() < lockoutUntil)}
            >
              {isLoading ? 'Завантаження...' : 'Увійти'}
            </button>
            
            <div className="login-footer">
              <button 
                type="button"
                className="forgot-password-button"
                onClick={handleForgotPassword}
                disabled={isLoading || (lockoutUntil && Date.now() < lockoutUntil)}
              >
                Забули пароль?
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

export default Login;
